/**
 * ReportingTable — shadcn/Tailwind-based entries table for the /team page.
 *
 * Two variants: ReportingTrackedTable (with checkboxes + actions) and
 * ReportingExportedTable (read-only). Both built on shared primitives.
 */

import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { ArrowDown, ArrowDownUp, ArrowUp, AlertTriangle } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Checkbox } from "@/shared/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { cn } from "@/shared/lib/utils";
import { useDarkMode } from "@/shared/hooks/useDarkMode";
import { HighlightedNarrative } from "@/shared/components/HighlightedNarrative/HighlightedNarrative";
import type { RestrictedWord } from "@/shared/cache/slices/restrictedWords";
import type { Matter } from "@/shared/cache/slices/matters";
import { isTaskRequired, isActivityRequired } from "@/shared/utils/firm";
import { formatTime } from "../EntriesTable/EntriesTable";

// App icons
import adobeIcon from "@/assets/applications/AdobeIcon.svg";
import calendarIcon from "@/assets/applications/CalendarIcon.svg";
import chromeIcon from "@/assets/applications/ChromeIcon.svg";
import combineIcon from "@/assets/applications/CombineIcon.svg";
import combineIconWhite from "@/assets/applications/CombineIconWhite.svg";
import duplicateIcon from "@/assets/applications/DuplicateIcon.svg";
import duplicateIconWhite from "@/assets/applications/DuplicateIconWhite.svg";
import excelIcon from "@/assets/applications/ExcelIcon.svg";
import foxitIcon from "@/assets/applications/FoxitIcon.svg";
import kofaxIcon from "@/assets/applications/KofaxIcon.svg";
import outlookIcon from "@/assets/applications/OutlookIcon.svg";
import phoneIcon from "@/assets/icons/phone.svg";
import phoneWhiteIcon from "@/assets/icons/phoneWhite.svg";
import photosIcon from "@/assets/applications/WindowsPhotos.svg";
import powerpointIcon from "@/assets/applications/PowerpointIcon.svg";
import teamsIcon from "@/assets/applications/TeamsIcon.svg";
import userInputIcon from "@/assets/icons/userInput.svg";
import userInputWhiteIcon from "@/assets/icons/userInputWhite.svg";
import wordIcon from "@/assets/applications/WordIcon.svg";
import zoomIcon from "@/assets/applications/ZoomIcon.svg";

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

export type EntryStatus = "tracked" | "exported" | "deleted";

export type Entry = {
  id: number;
  date: string;
  matter: string;
  matter_id?: string;
  matter_name?: string;
  client?: string;
  description: string;
  task: string;
  activity?: string;
  document_name?: string;
  duration: number;
  end_time?: string;
  application?: string;
  status?: EntryStatus;
  attorney_feedback?: string | null;
};

export type SortingMode = "time" | "matter";
export type SortDirection = "asc" | "desc";

// ─────────────────────────────────────────────
// App icon map
// ─────────────────────────────────────────────

const APP_ICON_MAP: Record<string, string> = {
  "Microsoft Word": wordIcon,
  "Microsoft Outlook": outlookIcon,
  "Microsoft Outlook Calendar": calendarIcon,
  "Adobe Acrobat": adobeIcon,
  "Microsoft Powerpoint": powerpointIcon,
  "Microsoft Excel": excelIcon,
  Zoom: zoomIcon,
  "Google Chrome": chromeIcon,
  "Microsoft Teams": teamsIcon,
  Kofax: kofaxIcon,
  Foxit: foxitIcon,
  Photos: photosIcon,
};

function AppIcon({ application, darkMode }: { application?: string; darkMode: boolean }) {
  if (!application) return null;
  let src: string | undefined;
  if (application === "Phone") src = darkMode ? phoneWhiteIcon : phoneIcon;
  else if (application === "User Input") src = darkMode ? userInputWhiteIcon : userInputIcon;
  else if (application === "Combine") src = darkMode ? combineIconWhite : combineIcon;
  else if (application === "Duplicate") src = darkMode ? duplicateIconWhite : duplicateIcon;
  else src = APP_ICON_MAP[application];
  if (!src) return null;
  return <img src={src} alt={application} className="h-[30px] w-[30px] min-w-[30px] shrink-0" />;
}

// ─────────────────────────────────────────────
// Status badge
// ─────────────────────────────────────────────

const badgeStyles: Record<string, string> = {
  exported: "bg-green-100 text-green-700 border-green-500/50 dark:bg-green-950/30 dark:text-green-400 dark:border-border",
  tracked: "bg-amber-100 text-amber-700 border-amber-500/50 dark:bg-amber-950/30 dark:text-amber-400 dark:border-border",
  incomplete: "bg-amber-100 text-amber-700 border-amber-500/50 dark:bg-amber-950/30 dark:text-amber-400 dark:border-border",
  failed: "bg-red-100 text-red-700 border-red-500/50 dark:bg-red-950/30 dark:text-red-400 dark:border-border",
  deleted: "bg-red-100 text-red-700 border-red-500/50 dark:bg-red-950/30 dark:text-red-400 dark:border-border",
  exporting: "bg-teal-100 text-teal-700 border-teal-500/50 dark:bg-teal-950/30 dark:text-teal-400 dark:border-border",
};

function StatusBadge({ label }: { label: string }) {
  return (
    <span className={cn("text-[0.6875rem] font-semibold px-2.5 py-1 rounded-full border whitespace-nowrap", badgeStyles[label.toLowerCase()] ?? "")}>
      {label}
    </span>
  );
}

// ─────────────────────────────────────────────
// Units badge
// ─────────────────────────────────────────────

function UnitsBadge({ duration }: { duration: number }) {
  const display = (duration * 100) % 10 !== 0 ? duration.toFixed(2) : duration.toFixed(1);
  return (
    <span className="inline-flex items-center justify-center min-w-[50px] h-[30px] px-2.5 rounded-md bg-[var(--brand-dark-green,hsl(var(--primary)))] text-white text-xs font-semibold tabular-nums border border-[var(--brand-dark-green,hsl(var(--primary)))]">
      {display}
    </span>
  );
}

// ─────────────────────────────────────────────
// Sort header with tooltip
// ─────────────────────────────────────────────

function sortTooltipText(column: SortingMode, sortingMode: SortingMode, sortDirection: SortDirection): string {
  if (sortingMode !== column) return column === "time" ? "Sort by time" : "Sort by matter";
  if (column === "time") return sortDirection === "desc" ? "Newest first" : "Oldest first";
  return sortDirection === "asc" ? "A to Z" : "Z to A";
}

function SortableHeader({ label, column, sortingMode, sortDirection, onClick }: {
  label: string;
  column: SortingMode;
  sortingMode: SortingMode;
  sortDirection: SortDirection;
  onClick?: () => void;
}) {
  const icon = sortingMode !== column
    ? <ArrowDownUp className="h-3 w-3 text-muted-foreground/50" />
    : sortDirection === "desc"
      ? <ArrowDown className="h-3 w-3" />
      : <ArrowUp className="h-3 w-3" />;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1 cursor-pointer select-none" onClick={onClick}>
            {label} {icon}
          </span>
        </TooltipTrigger>
        <TooltipContent>{sortTooltipText(column, sortingMode, sortDirection)}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ─────────────────────────────────────────────
// Incomplete check
// ─────────────────────────────────────────────

function checkIncomplete(entry: Entry, matters: Matter[]): boolean {
  const taskRequired = isTaskRequired(entry.matter_id, matters);
  const activityRequired = isActivityRequired(entry.matter_id, matters);
  return (
    !entry.matter ||
    !entry.description ||
    (taskRequired && !entry.task) ||
    (activityRequired && !entry.activity) ||
    entry.description === "Please fill in the narrative."
  );
}

// ─────────────────────────────────────────────
// Restricted words resolver
// ─────────────────────────────────────────────

function useResolvedRestrictedWords(
  entry: Entry,
  getRestrictedWords?: (client?: string, matter?: string) => RestrictedWord[],
): RestrictedWord[] | undefined {
  return useMemo(() => {
    if (!getRestrictedWords) return undefined;
    if (!entry.matter_id) return getRestrictedWords();
    const [client, ...rest] = entry.matter_id.split(".");
    const matter = rest.join(".");
    return getRestrictedWords(client, matter);
  }, [getRestrictedWords, entry.matter_id]);
}

// ─────────────────────────────────────────────
// Row left border
// ─────────────────────────────────────────────

function rowLeftBorder(entry: Entry, isIncomplete: boolean, failedExportEntries: Map<number, string>): string {
  if (failedExportEntries.has(entry.id)) return "shadow-[inset_3px_0_0_0_var(--color-destructive)]";
  if (isIncomplete) return "shadow-[inset_3px_0_0_0_var(--color-amber-500)]";
  return "";
}

// ─────────────────────────────────────────────
// Narrative cell
// ─────────────────────────────────────────────

function NarrativeCell({ entry, getRestrictedWords }: {
  entry: Entry;
  getRestrictedWords?: (client?: string, matter?: string) => RestrictedWord[];
}) {
  const restrictedWords = useResolvedRestrictedWords(entry, getRestrictedWords);

  return (
    <div className="flex items-start gap-1.5 overflow-hidden">
      {entry.attorney_feedback && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500 mt-0.5 cursor-pointer" />
            </TooltipTrigger>
            <TooltipContent>This narrative has unresolved feedback</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      <div className="line-clamp-3 text-sm break-words">
        <HighlightedNarrative
          text={entry.description || "\u2014"}
          restrictedWords={restrictedWords}
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Column width style (used on colgroup)
// ─────────────────────────────────────────────

// Tracked: checkbox(40px) icon(56px) date(7%) matter(25%) codes(7%) narrative(auto) units(15%)
// Exported: icon(56px) date(7%) matter(25%) codes(7%) narrative(auto) units(15%)

// ─────────────────────────────────────────────
// Shared table props
// ─────────────────────────────────────────────

interface BaseTableProps {
  entries: Entry[];
  groupedEntries: Record<string, Entry[]>;
  sortingMode: SortingMode;
  sortDirection: SortDirection;
  onSortByTime?: () => void;
  onSortByMatter?: () => void;
  onRowClick?: (entry: Entry) => void;
  exportingEntryIds?: Set<number>;
  mutatingIds?: Set<number>;
  failedExportEntries?: Map<number, string>;
  matters?: Matter[];
  getRestrictedWords?: (client?: string, matter?: string) => RestrictedWord[];
  emptyMessage?: string;
  autoScrollToBottom?: boolean;
}

// ─────────────────────────────────────────────
// ReportingTrackedTable
// ─────────────────────────────────────────────

interface TrackedTableProps extends BaseTableProps {
  checkedRows: Set<number>;
  onCheckedRowsChange: (rows: Set<number>) => void;
}

export function ReportingTrackedTable({
  entries,
  groupedEntries,
  sortingMode,
  sortDirection,
  onSortByTime,
  onSortByMatter,
  onRowClick,
  exportingEntryIds = new Set(),
  mutatingIds = new Set(),
  failedExportEntries = new Map() as Map<number, string>,
  matters = [],
  getRestrictedWords,
  emptyMessage = "No entries to show",
  autoScrollToBottom = false,
  checkedRows,
  onCheckedRowsChange,
}: TrackedTableProps) {
  const { darkMode } = useDarkMode();
  const lastClickedId = useRef<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasInitiallyScrolled = useRef(false);

  // Auto-scroll: to bottom for today, to top for other dates on first load
  useEffect(() => {
    if (!scrollRef.current || entries.length === 0) return;
    if (autoScrollToBottom) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    } else if (!hasInitiallyScrolled.current) {
      scrollRef.current.scrollTop = 0;
      hasInitiallyScrolled.current = true;
    }
  }, [autoScrollToBottom, groupedEntries, entries.length]);

  const flatIds = useMemo(() => {
    const ids: number[] = [];
    for (const key of Object.keys(groupedEntries)) {
      for (const entry of groupedEntries[key]) ids.push(entry.id);
    }
    return ids;
  }, [groupedEntries]);

  const isIncomplete = useCallback((entry: Entry) => checkIncomplete(entry, matters), [matters]);

  const selectionCount = checkedRows.size;
  const selectAllChecked = entries.length > 0 && selectionCount === entries.length;
  const selectAllIndeterminate = selectionCount > 0 && selectionCount < entries.length;

  const handleSelectAll = useCallback(() => {
    if (selectAllChecked || selectAllIndeterminate) {
      onCheckedRowsChange(new Set());
    } else {
      onCheckedRowsChange(new Set(entries.map((e) => e.id)));
    }
  }, [selectAllChecked, selectAllIndeterminate, entries, onCheckedRowsChange]);

  const handleRowSelect = useCallback((id: number, e: React.MouseEvent) => {
    const hasCtrl = e.ctrlKey || e.metaKey;
    const hasShift = e.shiftKey;

    if (hasShift && lastClickedId.current !== null) {
      const from = flatIds.indexOf(lastClickedId.current);
      const to = flatIds.indexOf(id);
      if (from !== -1 && to !== -1) {
        const start = Math.min(from, to);
        const end = Math.max(from, to);
        const rangeIds = flatIds.slice(start, end + 1);
        if (hasCtrl) {
          const next = new Set(checkedRows);
          for (const rid of rangeIds) next.add(rid);
          onCheckedRowsChange(next);
        } else {
          onCheckedRowsChange(new Set(rangeIds));
        }
        return;
      }
    }

    if (hasCtrl) {
      const next = new Set(checkedRows);
      if (next.has(id)) next.delete(id); else next.add(id);
      onCheckedRowsChange(next);
    } else {
      if (checkedRows.size === 1 && checkedRows.has(id)) {
        onCheckedRowsChange(new Set());
      } else {
        onCheckedRowsChange(new Set([id]));
      }
    }
    lastClickedId.current = id;
  }, [flatIds, checkedRows, onCheckedRowsChange]);

  if (entries.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center py-16 text-sm text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  const groups = Object.keys(groupedEntries);
  const COLS = 7;

  return (
    <div ref={scrollRef} className="flex-1 overflow-auto">
      <Table className="min-w-[1000px] table-fixed" wrapperClassName="overflow-visible">
        <colgroup>
          <col className="w-[40px]" />
          <col className="w-[56px]" />
          <col className="w-[8%]" />
          <col className="w-[24%]" />
          <col className="w-[8%]" />
          <col />
          <col className="w-[15%]" />
        </colgroup>
        <TableHeader className="sticky top-0 z-10 bg-muted/90 backdrop-blur-sm dark:bg-muted/95">
          <TableRow className="hover:bg-transparent border-b-0">
            <TableHead>
              <Checkbox
                checked={selectAllChecked}
                indeterminate={selectAllIndeterminate}
                onCheckedChange={handleSelectAll}
                aria-label="Select all"
              />
            </TableHead>
            <TableHead>
              {selectionCount > 0 && (
                <span className="text-[0.6875rem] font-semibold text-primary">{selectionCount}</span>
              )}
            </TableHead>
            <TableHead>
              <SortableHeader label="Date" column="time" sortingMode={sortingMode} sortDirection={sortDirection} onClick={onSortByTime} />
            </TableHead>
            <TableHead>
              <SortableHeader label="Matter" column="matter" sortingMode={sortingMode} sortDirection={sortDirection} onClick={onSortByMatter} />
            </TableHead>
            <TableHead>Codes</TableHead>
            <TableHead>Narrative</TableHead>
            <TableHead className="text-right">Units</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.map((groupKey) => {
            const groupEntries = groupedEntries[groupKey];
            const groupTotal = groupEntries.reduce((sum, e) => sum + e.duration, 0);
            return (
              <React.Fragment key={groupKey}>
                <TableRow className="sticky top-[theme(spacing.12)] -mt-px z-[5] bg-muted/90 backdrop-blur-sm hover:bg-muted/80 dark:bg-muted/80 dark:hover:bg-muted/70 border-b border-border">
                  <TableCell colSpan={COLS - 1} className="py-1.5 px-4">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{groupKey}</span>
                  </TableCell>
                  <TableCell className="py-1.5 text-right">
                    {sortingMode === "matter" && (
                      <span className="text-xs text-muted-foreground">{groupTotal.toFixed(1)} hrs</span>
                    )}
                  </TableCell>
                </TableRow>
                {groupEntries.map((entry) => {
                  const isChecked = checkedRows.has(entry.id);
                  const isExporting = exportingEntryIds.has(entry.id);
                  const isMutating = mutatingIds.has(entry.id);
                  const incomplete = isIncomplete(entry);
                  const isFailed = failedExportEntries.has(entry.id);
                  const borderClass = rowLeftBorder(entry, incomplete, failedExportEntries);

                  let statusLabel: string | null = null;
                  if (isExporting) statusLabel = "Exporting";
                  else if (entry.status === "exported") statusLabel = "Exported";
                  else if (entry.status === "deleted") statusLabel = "Deleted";
                  else if (entry.status === "tracked") statusLabel = "Tracked";
                  else if (isFailed) statusLabel = "Failed";
                  else if (incomplete) statusLabel = "Incomplete";

                  return (
                    <TableRow
                      key={entry.id}
                      data-state={isChecked ? "selected" : undefined}
                      className={cn(
                        "h-14 max-h-14 cursor-default transition-colors [&>td]:overflow-hidden",
                        "hover:bg-secondary/60 dark:hover:bg-card",
                        borderClass,
                        isExporting && "opacity-55 pointer-events-none cursor-not-allowed",
                        isMutating && "opacity-50 pointer-events-none transition-opacity duration-200",
                      )}
                      onClick={(e) => {
                        if (isExporting || isMutating) return;
                        if (e.shiftKey || e.ctrlKey || e.metaKey) {
                          e.preventDefault();
                          window.getSelection()?.removeAllRanges();
                          handleRowSelect(entry.id, e);
                          return;
                        }
                        onRowClick?.(entry);
                      }}
                    >
                      <TableCell className="py-2" onClick={(e) => {
                        // Only stop propagation for plain clicks (let shift/ctrl bubble to row handler)
                        if (!e.shiftKey && !e.ctrlKey && !e.metaKey) e.stopPropagation();
                      }}>
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={() => {
                            const next = new Set(checkedRows);
                            if (next.has(entry.id)) next.delete(entry.id); else next.add(entry.id);
                            onCheckedRowsChange(next);
                            lastClickedId.current = entry.id;
                          }}
                          aria-label={`Select entry ${entry.id}`}
                        />
                      </TableCell>
                      <TableCell className="py-2 px-2">
                        <AppIcon application={entry.application} darkMode={darkMode} />
                      </TableCell>
                      <TableCell className="py-2 text-center whitespace-nowrap text-xs">
                        <div>{entry.date}</div>
                        {entry.end_time && (
                          <div className="text-[0.6875rem] text-muted-foreground">{formatTime(entry.end_time)}</div>
                        )}
                      </TableCell>
                      <TableCell className="py-2">
                        <div className="flex flex-col gap-0.5 justify-center overflow-hidden">
                          {entry.client && (
                            <span className="text-[0.6875rem] text-muted-foreground truncate">{entry.client}</span>
                          )}
                          <span className="text-sm truncate">{entry.matter_name || entry.matter || "\u2014"}</span>
                          {entry.matter && (
                            <span className="text-xs text-muted-foreground truncate">{entry.matter}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="py-2">
                        <div className="flex flex-col gap-0.5 overflow-hidden">
                          <span className="text-xs truncate">{entry.task || "\u2014"}</span>
                          {entry.activity && <span className="text-xs text-muted-foreground truncate">{entry.activity}</span>}
                        </div>
                      </TableCell>
                      <TableCell className="py-2">
                        <NarrativeCell entry={entry} getRestrictedWords={getRestrictedWords} />
                      </TableCell>
                      <TableCell className="py-2 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {statusLabel && <StatusBadge label={statusLabel} />}
                          <UnitsBadge duration={entry.duration} />
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

// ─────────────────────────────────────────────
// ReportingExportedTable
// ─────────────────────────────────────────────

interface ExportedTableProps extends BaseTableProps {}

export function ReportingExportedTable({
  entries,
  groupedEntries,
  sortingMode,
  sortDirection,
  onSortByTime,
  onSortByMatter,
  onRowClick,
  exportingEntryIds = new Set(),
  mutatingIds = new Set(),
  failedExportEntries: _failedExportEntries = new Map() as Map<number, string>,
  matters: _matters = [],
  getRestrictedWords,
  emptyMessage = "No exported entries",
  autoScrollToBottom = false,
}: ExportedTableProps) {
  const { darkMode } = useDarkMode();
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasInitiallyScrolled = useRef(false);

  useEffect(() => {
    if (!scrollRef.current || entries.length === 0) return;
    if (autoScrollToBottom) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    } else if (!hasInitiallyScrolled.current) {
      scrollRef.current.scrollTop = 0;
      hasInitiallyScrolled.current = true;
    }
  }, [autoScrollToBottom, groupedEntries, entries.length]);

  if (entries.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center py-16 text-sm text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  const groups = Object.keys(groupedEntries);
  const COLS = 6;

  return (
    <div ref={scrollRef} className="flex-1 overflow-auto">
      <Table className="min-w-[1000px] table-fixed" wrapperClassName="overflow-visible">
        <colgroup>
          <col className="w-[56px]" />
          <col className="w-[8%]" />
          <col className="w-[24%]" />
          <col className="w-[8%]" />
          <col />
          <col className="w-[15%]" />
        </colgroup>
        <TableHeader className="sticky top-0 z-10 bg-muted/90 backdrop-blur-sm dark:bg-muted/95">
          <TableRow className="hover:bg-transparent border-b-0">
            <TableHead />
            <TableHead>
              <SortableHeader label="Date" column="time" sortingMode={sortingMode} sortDirection={sortDirection} onClick={onSortByTime} />
            </TableHead>
            <TableHead>
              <SortableHeader label="Matter" column="matter" sortingMode={sortingMode} sortDirection={sortDirection} onClick={onSortByMatter} />
            </TableHead>
            <TableHead>Codes</TableHead>
            <TableHead>Narrative</TableHead>
            <TableHead className="text-right">Units</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.map((groupKey) => {
            const groupEntries = groupedEntries[groupKey];
            const groupTotal = groupEntries.reduce((sum, e) => sum + e.duration, 0);
            return (
              <React.Fragment key={groupKey}>
                <TableRow className="sticky top-[theme(spacing.12)] -mt-px z-[5] bg-muted/90 backdrop-blur-sm hover:bg-muted/80 dark:bg-muted/80 dark:hover:bg-muted/70 border-b border-border">
                  <TableCell colSpan={COLS - 1} className="py-1.5 px-4">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{groupKey}</span>
                  </TableCell>
                  <TableCell className="py-1.5 text-right">
                    {sortingMode === "matter" && (
                      <span className="text-xs text-muted-foreground">{groupTotal.toFixed(1)} hrs</span>
                    )}
                  </TableCell>
                </TableRow>
                {groupEntries.map((entry) => {
                  const isExporting = exportingEntryIds.has(entry.id);
                  const isMutating = mutatingIds.has(entry.id);

                  return (
                    <TableRow
                      key={entry.id}
                      className={cn(
                        "h-14 max-h-14 cursor-default transition-colors [&>td]:overflow-hidden",
                        "hover:bg-secondary/60 dark:hover:bg-card",
                        isExporting && "opacity-55 pointer-events-none cursor-not-allowed",
                        isMutating && "opacity-50 pointer-events-none transition-opacity duration-200",
                      )}
                      onClick={() => {
                        if (isExporting || isMutating) return;
                        onRowClick?.(entry);
                      }}
                    >
                      <TableCell className="py-2 px-2">
                        <AppIcon application={entry.application} darkMode={darkMode} />
                      </TableCell>
                      <TableCell className="py-2 text-center whitespace-nowrap text-xs">
                        <div>{entry.date}</div>
                        {entry.end_time && (
                          <div className="text-[0.6875rem] text-muted-foreground">{formatTime(entry.end_time)}</div>
                        )}
                      </TableCell>
                      <TableCell className="py-2">
                        <div className="flex flex-col gap-0.5 justify-center overflow-hidden">
                          {entry.client && (
                            <span className="text-[0.6875rem] text-muted-foreground truncate">{entry.client}</span>
                          )}
                          <span className="text-sm truncate">{entry.matter_name || entry.matter || "\u2014"}</span>
                          {entry.matter && (
                            <span className="text-xs text-muted-foreground truncate">{entry.matter}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="py-2">
                        <div className="flex flex-col gap-0.5 overflow-hidden">
                          <span className="text-xs truncate">{entry.task || "\u2014"}</span>
                          {entry.activity && <span className="text-xs text-muted-foreground truncate">{entry.activity}</span>}
                        </div>
                      </TableCell>
                      <TableCell className="py-2">
                        <NarrativeCell entry={entry} getRestrictedWords={getRestrictedWords} />
                      </TableCell>
                      <TableCell className="py-2 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <StatusBadge label="Exported" />
                          <UnitsBadge duration={entry.duration} />
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
